import {PAY_AUTH_DOMAIN, PAY_AUTH_DOMAIN_ALT} from '~/constants/authorize';
import {Bugsnag} from '~/foundation/Bugsnag/Bugsnag';
import type {OAuthParams} from '~/types/oauth';
import {isoDocument} from '~/utils/document';
import {IFrameEventSource, postMessage} from '~/utils/postMessage';
import {isRootDomainMatch} from '~/utils/validators';
import {isoWindow} from '~/utils/window';

import {buildShopLoginInputUrl} from './buildShopLoginInputUrl';

interface Props {
  analyticsContext: string;
  analyticsTraceId: string;
  attributes: OAuthParams;
  flow: string;
  hiddenClassName: string;
  loader: HTMLElement | null;
  onLoaded: () => void;
  target: HTMLInputElement | null;
  targetWrapper: HTMLElement | null;
}

export default function initShopLoginInput({
  analyticsContext,
  analyticsTraceId,
  attributes,
  flow,
  hiddenClassName,
  loader,
  onLoaded,
  target,
  targetWrapper,
}: Props) {
  const {notify} = new Bugsnag('ShopLoginInput');

  if (!target) {
    notify(
      new Error('Target was not provided when initializing ShopLoginInput'),
    );
    return;
  }

  if (!targetWrapper) {
    notify(
      new Error(
        'TargetWrapper was not provided when initializing ShopLoginInput',
      ),
    );
    return;
  }

  if (!hiddenClassName) {
    notify(
      new Error(
        'hiddenClassName was not provided when initializing ShopLoginInput',
      ),
    );
    return;
  }

  // create iframe element, and hide it with the given class
  const iframeElement = isoDocument.createElement('iframe');
  iframeElement.setAttribute('style', `border: none; margin-top: 0.75rem;`);
  iframeElement.allow = 'publickey-credentials-get *';
  iframeElement.height = '0';
  iframeElement.id = 'shop-login-email-input-iframe';
  iframeElement.src = buildShopLoginInputUrl({
    ...attributes,
    analyticsTraceId,
    analyticsContext,
    flow,
  });
  iframeElement.tabIndex = 0;
  iframeElement.width = '100%';
  iframeElement.classList.add(hiddenClassName);

  // add hidden iframe to wrapper of input, to trigger loading of iframe content
  targetWrapper?.parentNode?.insertBefore(iframeElement, targetWrapper);

  const eventSource = new IFrameEventSource(iframeElement);

  // set up the listener
  const listener = (event: MessageEvent) => {
    if (!eventSource?.isSourceOf(event)) {
      return;
    }

    const eventOrigins = [
      PAY_AUTH_DOMAIN,
      PAY_AUTH_DOMAIN_ALT,
      isoWindow?.location?.origin,
    ];
    // Ignore events for which the root domain doesn't match that of the allowlist in eventOrigins
    if (
      !eventOrigins.some((origin) => isRootDomainMatch(origin, event.origin))
    ) {
      // eslint-disable-next-line no-console
      console.error('Origin mismatch for message event', event);
      return;
    }

    handlePostMessage(event.data);
  };
  isoWindow.addEventListener('message', listener, false);

  const handleInputChange = (event: Event) => {
    const element = event.target as HTMLInputElement;
    postMessage({
      contentWindow: iframeElement.contentWindow,
      event: {
        type: 'emailsubmitted',
        email: element.value,
      },
    });
  };
  target?.addEventListener('change', handleInputChange);

  /**
   * When the iframe is loaded, check the loader status to determine whether the iframe should be made visible.
   * - if so, show itself and dispatch event on the loader
   * - if not, emit an error and remove iframe from DOM
   */
  function handleLoaded() {
    if (!loader || loader?.getAttribute('data-status') === 'shop-loading') {
      iframeElement.classList.remove(hiddenClassName);
      loader?.dispatchEvent(new Event('shopinputloaded'));
      onLoaded();
      return;
    }

    // if the loading took too long, do not risk providing a bad experience
    isoWindow.removeEventListener('message', listener, false);
    iframeElement.remove();
    target?.removeEventListener('change', handleInputChange);
    notify(new Error('Input iframe is loading too slow.'));
  }

  function handleError(message: string) {
    loader?.dispatchEvent(
      new CustomEvent('processingerror', {
        detail: {message},
      }),
    );
  }

  function handleEnterPressed(): void {
    if (!target?.form) {
      notify(new Error("Could not find target's form"));
    }
    target!.form!.requestSubmit();
  }

  function handleResize(newHeight: string): void {
    iframeElement.height = newHeight;
  }

  function handleChange(newValue: string): void {
    if (!target) {
      notify(
        new Error(
          'Target was unavailable when handling ShopLoginInput value change',
        ),
      );
      return;
    }

    if (target.value !== newValue) {
      target.value = newValue;
      target.dispatchEvent(new Event('input'));
    }
  }

  function handlePostMessage(data: string): void {
    let parsed;
    try {
      const raw = data.replace('shopify_pay:', '');
      parsed = JSON.parse(raw);
    } catch (_exception) {
      notify(
        new Error(`postMessage payload did not match expected shape: ${data}`),
      );
    }
    switch (parsed.action) {
      case 'changed':
        handleChange(parsed.value);
        break;
      case 'enterPressed':
        handleEnterPressed();
        break;
      case 'error':
        handleError(parsed.message);
        break;
      case 'loaded':
        handleLoaded();
        break;
      case 'loggedIn':
        isoWindow.removeEventListener('message', listener, false);
        break;
      case 'processing':
        loader?.dispatchEvent(new Event('processing'));
        break;
      case 'resized':
        handleResize(parsed.height);
        break;
      default:
    }
  }
}
